import { AxiosInstance, AxiosResponse } from "axios"
import { downloadResponseAsFile } from "utils/files/downloadUtils"
import {
  SQL_EXPORT_TYPES,
  EXPORT_VARIABLE_TYPES,
  DATA_FORMAT,
  CSV_CAT_OPTION,
} from "types/engineExport.types"
import { QueryInstance } from "src/types/engine.types"
import { pollAsync, fetchAsyncResult } from "api/Engine/data-reporting"

/**
 * Export API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Exports data as a JSON file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @param dataFormat - The data format (categorized or expanded).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToJSON(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  filename: string,
  dataFormat: DATA_FORMAT = DATA_FORMAT.CATEGORIZED
): Promise<AxiosResponse<Blob>> {
  const config = {
    responseType: "blob" as const, // Important to download files as binary
  }

  const projectName = encodeURIComponent(filename || "export")
  const response = await axiosInstance.get(
    `/export-json?instanceId=${instanceID}&project=${projectName}&dataformat=${dataFormat}`,
    config
  )

  downloadResponseAsFile(
    response,
    filename ? `${filename}.json` : "export.json"
  )
  return response
}

/**
 * Exports data as a JSONL (JSON Lines) zip file from the given instance and triggers a download.
 * The zip contains a schema.json and a .jsonl data file (one JSON object per respondent per line).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToJSONL(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  filename: string,
  useLabels: boolean = false,
  flatSeparator: string = ""
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      responseType: "text" as const,
    }

    const projectName = encodeURIComponent(filename || "export")
    const labelsParam = useLabels ? "&labels" : ""
    const flatParam = flatSeparator ? `&flat=${flatSeparator}` : ""
    const response = await axiosInstance.get(
      `/export-json?instanceId=${instanceID}&project=${projectName}&jsonl${labelsParam}${flatParam}`,
      config
    )

    const base64Data = response.data.trim()
    const binaryString = window.atob(base64Data)
    const len = binaryString.length
    const uint8Array = new Uint8Array(len)

    for (let i = 0; i < len; i++) {
      uint8Array[i] = binaryString.charCodeAt(i)
    }

    const blob = new Blob([uint8Array], { type: "application/zip" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${filename || "export"}.zip`

    document.body.appendChild(link)
    link.click()

    window.URL.revokeObjectURL(url)
    link.remove()

    return response
  } catch (error) {
    console.error("Error downloading JSONL file:", error)
    throw error
  }
}

/**
 * Exports data as a SQL file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param sqlType - The type of SQL export ("wide" or "narrow").
 * @param project - The project name associated with the SQL export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToSQL(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  sqlType: SQL_EXPORT_TYPES,
  project: string,
  filename: string
): Promise<AxiosResponse<Blob>> {
  const config = {
    responseType: "blob" as const, // Important to download files as binary
  }

  const response = await axiosInstance.get(
    `/export-sql?instanceId=${instanceID}&sqlType=${sqlType}&project=${project}`,
    config
  )

  downloadResponseAsFile(response, filename ? `${filename}.sql` : "export.sql")
  return response
}

/**
 * Exports data as an SPS file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param varType - The variable type to include in the export ("numbering" or "labels").
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToSPS(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES,
  filename: string
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      responseType: "text" as const, // Expecting base64 encoded string
    }

    const response = await axiosInstance.get(
      `/export-sps?instanceId=${instanceID}&varType=${varType}&spsType=sps_dat&project=${encodeURIComponent(filename || "export")}`,
      config
    )

    // Get the base64-encoded string and trim any whitespace
    const base64Data = response.data.trim()

    // Convert base64 to binary
    const binaryString = window.atob(base64Data)
    const len = binaryString.length
    const uint8Array = new Uint8Array(len)

    for (let i = 0; i < len; i++) {
      uint8Array[i] = binaryString.charCodeAt(i)
    }

    // Create blob from the binary data
    const blob = new Blob([uint8Array], { type: "application/zip" })

    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${filename || "export"}.zip`

    // Trigger download
    document.body.appendChild(link)
    link.click()

    // Cleanup
    window.URL.revokeObjectURL(url)
    link.remove()

    return response
  } catch (error) {
    console.error("Error downloading zip file:", error)
    throw error
  }
}

const ASYNC_POLL_INTERVAL_MS = 5000
const ASYNC_MAX_WAIT_MS = 20 * 60 * 1000 // 20-minute safety cap

/**
 * Resolves the base64 payload of an export whether the server finished it
 * synchronously (200, body is the base64) or handed back an async job id
 * (202, body is the id → poll /async-ready → fetch /async-get).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param submitResponse - The response from the initial export submit request
 * @returns The trimmed base64 string of the exported (zipped) file
 * @throws If the submit status is neither 200 nor 202, or the wait cap is exceeded
 */
export async function resolveAsyncBase64(
  axiosInstance: AxiosInstance,
  submitResponse: AxiosResponse<string>
): Promise<string> {
  // Fast path: the engine finished within the server's synchronous wait window.
  if (submitResponse.status === 200) {
    return submitResponse.data.trim()
  }
  if (submitResponse.status !== 202) {
    throw new Error(`Unexpected export status ${submitResponse.status}`)
  }

  // Async path: the 202 body is the plain-string job id.
  const asyncID = submitResponse.data.trim()
  const started = Date.now()

  for (;;) {
    if (Date.now() - started > ASYNC_MAX_WAIT_MS) {
      throw new Error("Export timed out waiting for the server to finish")
    }
    await new Promise((resolve) => setTimeout(resolve, ASYNC_POLL_INTERVAL_MS))

    const ready = await pollAsync(axiosInstance, asyncID)
    if (ready.status === 200) {
      const result = await fetchAsyncResult(axiosInstance, asyncID)
      return String(result.data).trim()
    }
    // status 202 => still running; keep polling.
  }
}

/**
 * Exports data as a SAV (SPSS binary) file from the given instance and triggers a download.
 * This produces a .sav file directly, without requiring SPSS to process SPS syntax.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param varType - The variable type to include in the export ("numbering" or "labels").
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToSAV(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES,
  filename: string
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      responseType: "text" as const,
      headers: { "ASync-Request": 10 },
    }

    const projectName = encodeURIComponent(filename || "export")
    const response = await axiosInstance.get<string>(
      `/export-sps?instanceId=${instanceID}&varType=${varType}&project=${projectName}&sav`,
      config
    )

    const base64Data = await resolveAsyncBase64(axiosInstance, response)
    const binaryString = window.atob(base64Data)
    const len = binaryString.length
    const uint8Array = new Uint8Array(len)

    for (let i = 0; i < len; i++) {
      uint8Array[i] = binaryString.charCodeAt(i)
    }

    const blob = new Blob([uint8Array], { type: "application/zip" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${filename || "export"}.zip`

    document.body.appendChild(link)
    link.click()

    window.URL.revokeObjectURL(url)
    link.remove()

    return response
  } catch (error) {
    console.error("Error downloading SAV file:", error)
    throw error
  }
}

/**
 * Exports data as a DQD (DataQ Binary) file from the given instance and triggers a download.
 * DQD is DataQ's internal binary format for efficient data storage.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @param dataFormat - The data format (categorized or expanded).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToDQD(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  filename: string,
  dataFormat: DATA_FORMAT = DATA_FORMAT.CATEGORIZED
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      responseType: "text" as const, // Expecting base64 encoded string
    }

    const response = await axiosInstance.get(
      `/export-dqd?instanceId=${instanceID}&dataformat=${dataFormat}`,
      config
    )

    // Get the base64-encoded string and trim any whitespace
    const base64Data = response.data.trim()

    // Convert base64 to binary
    const binaryString = window.atob(base64Data)
    const len = binaryString.length
    const uint8Array = new Uint8Array(len)

    for (let i = 0; i < len; i++) {
      uint8Array[i] = binaryString.charCodeAt(i)
    }

    // Create blob from the binary data
    const blob = new Blob([uint8Array], { type: "application/octet-stream" })

    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${filename || "export"}.dqd`

    // Trigger download
    document.body.appendChild(link)
    link.click()

    // Cleanup
    window.URL.revokeObjectURL(url)
    link.remove()

    return response
  } catch (error) {
    console.error("Error downloading DQD file:", error)
    throw error
  }
}

/**
 * Exports data as a CSV file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param varType - The variable type to include in the export ("numbering" or "labels").
 * @param catOption - The categorical export option.
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToCSV(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES = EXPORT_VARIABLE_TYPES.LABELS,
  catOption: CSV_CAT_OPTION = CSV_CAT_OPTION.NORMAL,
  filename: string = "export"
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      responseType: "text" as const, // Expecting base64 encoded string
    }

    const response = await axiosInstance.get(
      `/export-csv?instanceId=${instanceID}&varType=${varType}&cat_option=${catOption}&project=${encodeURIComponent(filename || "export")}`,
      config
    )

    return response
  } catch (error) {
    console.error("Error downloading CSV file:", error)
    throw error
  }
}
